// The body of POST /api/v1/feedback: what `@echoessh/sdk` 1.0.0 sends. The
// limits and the error messages are v1's, because apps in production already
// handle them.
import { TICKET_CATEGORIES, type TicketCategory } from "./validators";

export const MAX_MESSAGE_LENGTH = 10_000;
export const MAX_USER_IDENTIFIER_LENGTH = 255;
export const MAX_METADATA_BYTES = 16 * 1024;
/** Room for the longest valid message in UTF-8 plus the largest metadata. */
export const MAX_BODY_BYTES = 64 * 1024;
const MAX_TITLE_LENGTH = 120;

export type Feedback = {
  category: TicketCategory;
  message: string;
  userIdentifier: string | null;
  metadata: Record<string, unknown> | null;
};

export type ParseResult =
  | { ok: true; value: Feedback }
  | { ok: false; error: string };

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isCategory(value: unknown): value is TicketCategory {
  return (TICKET_CATEGORIES as readonly unknown[]).includes(value);
}

export function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

const LONE_SURROGATE =
  /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;

/**
 * Whether a string is valid Unicode. JSON can carry half of a surrogate pair
 * ("\ud83d" on its own), and Convex refuses to store one, so text that fails
 * this is turned away as a bad payload instead of failing the write.
 */
export function isWellFormedText(text: string): boolean {
  return !LONE_SURROGATE.test(text);
}

/**
 * The first `max` characters of a string, counted as code points. Cutting by
 * `slice` counts UTF-16 units and can leave half an emoji at the end.
 */
export function truncateText(text: string, max: number): string {
  if (text.length <= max) return text;
  return Array.from(text).slice(0, max).join("");
}

export function parseFeedback(text: string): ParseResult {
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    return { ok: false, error: "Invalid JSON body" };
  }
  if (!isRecord(body)) return { ok: false, error: "Invalid JSON body" };

  const { category, message, userIdentifier, metadata } = body;
  if (!category) return { ok: false, error: "Category is required" };
  if (!isCategory(category)) {
    return {
      ok: false,
      error: `Invalid category. Must be one of: ${TICKET_CATEGORIES.join(", ")}`,
    };
  }
  if (!message || typeof message !== "string") {
    return { ok: false, error: "Message is required and must be a string" };
  }
  if (message.trim().length === 0) {
    return { ok: false, error: "Message cannot be empty" };
  }
  if (!isWellFormedText(message)) {
    return { ok: false, error: "Message contains invalid characters" };
  }
  if (message.length > MAX_MESSAGE_LENGTH) {
    return {
      ok: false,
      error: `Message exceeds maximum length of ${MAX_MESSAGE_LENGTH} characters`,
    };
  }
  const identifierError = userIdentifierError(userIdentifier);
  if (identifierError) return { ok: false, error: identifierError };
  const metadataError = metadataErrorOf(metadata);
  if (metadataError) return { ok: false, error: metadataError };

  return {
    ok: true,
    value: {
      category,
      message: message.trim(),
      userIdentifier: cleanUserIdentifier(userIdentifier),
      metadata: isRecord(metadata) ? metadata : null,
    },
  };
}

// The checks of `userIdentifier` and `metadata`, as v1 made them. Ratings
// (lib/ratingPayload.ts) take the two fields the same way.

/** What is wrong with a `userIdentifier`, or null when it is fine or absent. */
export function userIdentifierError(userIdentifier: unknown): string | null {
  if (userIdentifier === undefined || userIdentifier === null) return null;
  if (typeof userIdentifier !== "string") return "userIdentifier must be a string";
  if (!isWellFormedText(userIdentifier)) return "userIdentifier contains invalid characters";
  if (userIdentifier.length > MAX_USER_IDENTIFIER_LENGTH) {
    return `userIdentifier exceeds maximum length of ${MAX_USER_IDENTIFIER_LENGTH} characters`;
  }
  return null;
}

/** A checked `userIdentifier`, trimmed, or null when it says nothing. */
export function cleanUserIdentifier(userIdentifier: unknown): string | null {
  return typeof userIdentifier === "string" && userIdentifier.trim() ? userIdentifier.trim() : null;
}

/** What is wrong with `metadata`, or null when it is fine or absent. */
export function metadataErrorOf(metadata: unknown): string | null {
  if (metadata === undefined || metadata === null) return null;
  if (!isRecord(metadata)) return "metadata must be an object";
  if (byteLength(JSON.stringify(metadata)) > MAX_METADATA_BYTES) {
    return "metadata exceeds maximum size of 16KB";
  }
  return null;
}

/** A ticket's title when the source gives none: the first line that says something. */
export function titleOf(message: string): string {
  const line =
    message
      .split("\n")
      .map((part) => part.trim().replace(/\s+/g, " "))
      .find((part) => part.length > 0) ?? "";
  return Array.from(line).length > MAX_TITLE_LENGTH
    ? `${truncateText(line, MAX_TITLE_LENGTH - 1).trimEnd()}…`
    : line;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A lowercased email address, or null when the value is not one. */
export function asEmail(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const email = value.trim().toLowerCase();
  return email.length <= 254 && isWellFormedText(email) && EMAIL.test(email)
    ? email
    : null;
}

/**
 * Who sent this, as far as the payload says. The identifier is the app's own;
 * the email is the identifier itself when it is one, else `metadata.email`,
 * and is what makes a reply deliverable.
 */
export function contactHints(feedback: Pick<Feedback, "userIdentifier" | "metadata">): {
  externalId: string | null;
  email: string | null;
  name: string | null;
} {
  const name = feedback.metadata?.name;
  return {
    externalId: feedback.userIdentifier,
    email: asEmail(feedback.userIdentifier) ?? asEmail(feedback.metadata?.email),
    name:
      typeof name === "string" && name.trim() && isWellFormedText(name)
        ? truncateText(name.trim(), MAX_USER_IDENTIFIER_LENGTH)
        : null,
  };
}

export type Device = {
  userAgent?: string;
  browser?: string;
  os?: string;
  device?: string;
  ip?: string;
  language?: string;
  referer?: string;
};

/** What the request itself tells us about where it came from, as v1 recorded it. */
export function describeDevice(headers: {
  userAgent: string | null;
  language: string | null;
  referer: string | null;
  ip: string | null;
}): Device {
  const ua = headers.userAgent ?? "";
  const device: Device = {};
  if (headers.userAgent) device.userAgent = headers.userAgent.slice(0, 512);

  if (ua.includes("Firefox/")) device.browser = "Firefox";
  else if (ua.includes("Edg/")) device.browser = "Edge";
  else if (ua.includes("OPR/") || ua.includes("Opera")) device.browser = "Opera";
  else if (ua.includes("Chrome/")) device.browser = "Chrome";
  else if (ua.includes("Safari/")) device.browser = "Safari";

  if (ua.includes("Windows NT 10")) device.os = "Windows 10/11";
  else if (ua.includes("Windows NT")) device.os = "Windows";
  else if (ua.includes("Android")) device.os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) device.os = "iOS";
  else if (ua.includes("Mac OS X")) device.os = "macOS";
  else if (ua.includes("Linux")) device.os = "Linux";

  if (ua) {
    if (ua.includes("iPad") || ua.includes("Tablet")) device.device = "Tablet";
    else if (ua.includes("Mobile") || ua.includes("Android") || ua.includes("iPhone")) {
      device.device = "Mobile";
    } else device.device = "Desktop";
  }

  if (headers.ip) device.ip = headers.ip.slice(0, 64);
  const language = headers.language?.split(",")[0]?.trim();
  if (language) device.language = language.slice(0, 35);
  if (headers.referer) device.referer = headers.referer.slice(0, 512);
  return device;
}
